import {apiGet, apiPost} from "../../shared/api/client";
import {walletTotal} from "../../shared/wallets";

export const loadPartnerDashboard = () => apiGet("/api/v2/admin/partner/dashboard");

export const loadDeployerDashboard = () => apiGet("/api/v2/admin/deployer/dashboard");

export const createDeployerInvite = (payload = {}) =>
    apiPost("/api/v2/admin/deployer/invites", {
        code: payload.code || "",
        max_uses: payload.max_uses ?? null,
    });

export const loadDeployerLicenses = () => apiGet("/api/v2/admin/deployer/licenses");

export const requestDeployerLicenses = (quantity = 1) =>
    apiPost("/api/v2/admin/deployer/licenses/request", {quantity});

export const loadDeployerPartnerStaff = (placeId) =>
    apiGet(`/api/v2/admin/deployer/partners/${placeId}/staff`);

const settledValue = (result, fallback) => {
    if (result.status === "fulfilled") {
        return result.value;
    }
    return fallback;
};

export const loadOverview = async () => {
    const [
        role,
        places,
        users,
        settlements,
        notifications,
        placeOperations,
        contentOperations,
        notificationOperations,
    ] = await Promise.allSettled([
        apiGet("/api/v2/admin/role"),
        apiGet("/api/v2/admin/places"),
        apiGet("/api/v2/admin/users"),
        apiGet("/api/v2/admin/settlements"),
        apiGet("/api/v2/admin/notifications"),
        apiGet("/api/v2/admin/places-summary/operations"),
        apiGet("/api/v2/admin/content/operations-summary"),
        apiGet("/api/v2/admin/notifications/operations"),
    ]);

    const usersPayload = settledValue(users, {});
    const placesPayload = settledValue(places, {});
    const settlementsPayload = settledValue(settlements, {});
    const notificationsPayload = settledValue(notifications, {});
    const placeOperationsPayload = settledValue(placeOperations, {});
    const contentOperationsPayload = settledValue(contentOperations, {});
    const notificationOperationsPayload = settledValue(notificationOperations, {});

    return {
        role: settledValue(role, {}),
        organizations: {
            count: placesPayload.count ?? 0,
            connected: places.status === "fulfilled",
        },
        users: {
            count: usersPayload.count ?? 0,
            mainBalance: walletTotal(usersPayload, "main") ?? "0.00",
            plusBalance: walletTotal(usersPayload, "plus") ?? "0.00",
            extraBalance: walletTotal(usersPayload, "extra") ?? "0.00",
            frozenBalance: usersPayload.bonus_frozen_balance ?? "0.00",
            connected: users.status === "fulfilled",
        },
        settlements: {
            count: settlementsPayload.count ?? 0,
            draftCount: (settlementsPayload.results || []).filter((item) => item.status === "draft").length,
            paidCount: (settlementsPayload.results || []).filter((item) => item.status === "paid").length,
            connected: settlements.status === "fulfilled",
        },
        inbox: {
            notifications:
                notificationsPayload.count ??
                notificationsPayload.notifications?.length ??
                (Array.isArray(notificationsPayload) ? notificationsPayload.length : 0),
            connected: notifications.status === "fulfilled",
        },
        operations: {
            places: {
                missingLegal: placeOperationsPayload.places_missing_legal ?? 0,
                missingMedia: placeOperationsPayload.places_missing_media ?? 0,
                branchesWithoutGallery: placeOperationsPayload.branches_without_gallery ?? 0,
                branchesWithoutStaff: placeOperationsPayload.branches_without_staff ?? 0,
                hiddenPlaces: placeOperationsPayload.hidden_places ?? 0,
                hiddenBranches: placeOperationsPayload.hidden_branches ?? 0,
                connected: placeOperations.status === "fulfilled",
            },
            content: {
                hiddenArticles: contentOperationsPayload.hidden_articles ?? 0,
                inactiveBanners: contentOperationsPayload.inactive_banners ?? 0,
                bannersWithoutContent: contentOperationsPayload.banners_without_content ?? 0,
                inactiveStories: contentOperationsPayload.inactive_stories ?? 0,
                storiesWithoutPreview: contentOperationsPayload.stories_without_preview ?? 0,
                storiesWithoutItems: contentOperationsPayload.stories_without_items ?? 0,
                connected: contentOperations.status === "fulfilled",
            },
            notifications: {
                pendingCount: notificationOperationsPayload.pending_count ?? 0,
                changedCount: notificationOperationsPayload.changed_count ?? 0,
                rejectedCount: notificationOperationsPayload.rejected_count ?? 0,
                connected: notificationOperations.status === "fulfilled",
            },
        },
    };
};
